import React, { useState, useEffect, useCallback } from "react";
import { useSearchParams, useLocation } from "react-router-dom";

// Amplify
import { Analytics } from "aws-amplify";

import Select, { SelectOption } from "@amzn/meridian/select";
import Column from "@amzn/meridian/column";
import Row from "@amzn/meridian/row";
import Text from "@amzn/meridian/text";
import Loader from "@amzn/meridian/loader";
import PageLayout from "../components/app/page-layout";

import NetworkScreen from "../screens/network-screen";

import useWarehouseData from "../hooks/use-warehouse-data";
import useSelectedOptions from "../hooks/use-selected-options";

// Helpers
import { mapRegion } from "../utilities/map-region";
import { mapNetwork } from "../utilities/map-network";
import { multiSiteLeadMap } from "../utilities/multiSiteLeadMap";
import { subRegionMap, DEFAULT_SUB_REGION } from "../utilities/subRegionMap";

const NetworkTab = () => {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const routeOptionKey = "/summary/network";
  const [selectedOptions, setSelectedOptions] = useSelectedOptions();

  if (searchParams.get("region") && searchParams.get("country") && searchParams.get("network") && searchParams.get("group")
    && !selectedOptions[routeOptionKey].region && !selectedOptions[routeOptionKey].country
    && !selectedOptions[routeOptionKey].network && !selectedOptions[routeOptionKey].group
    && location.pathname.includes("network")
  ) {
    const newSelectedOptions = JSON.parse(JSON.stringify(selectedOptions));
    newSelectedOptions[routeOptionKey] = {
      "region": searchParams.get("region"),
      "country": searchParams.get("country"),
      "network": searchParams.get("network"),
      "group": searchParams.get("group")
    }
    setSelectedOptions(newSelectedOptions);
  } else if (!searchParams.get("region") && !searchParams.get("country") && !searchParams.get("network") && !searchParams.get("group")
    && selectedOptions[routeOptionKey].region && selectedOptions[routeOptionKey].country
    && selectedOptions[routeOptionKey].network && selectedOptions[routeOptionKey].group
    && location.pathname.includes("network")
  ) {
    setSearchParams(
      {
        "region": selectedOptions[routeOptionKey].region,
        "country": selectedOptions[routeOptionKey].country,
        "network": selectedOptions[routeOptionKey].network,
        "group": selectedOptions[routeOptionKey].group
      }
    );
  }

  // NA, EU, FE etc
  const [region, setRegion] = useState(searchParams.get("region") ? searchParams.get("region") : selectedOptions[routeOptionKey].region);
  const [regions, setRegions] = useState();

  // USA, GB, IT etc
  const [country, setCountry] = useState(searchParams.get("country") ? searchParams.get("country") : selectedOptions[routeOptionKey].country);
  const [countries, setCountries] = useState([]);
  // FCs, Stores, Network
  const [network, setNetwork] = useState(searchParams.get("network") ? searchParams.get("network") : selectedOptions[routeOptionKey].network);
  const [networks, setNetworks] = useState([]);
  // Sub Region
  const [subRegion, setSubRegion] = useState(searchParams.get("subRegion") ? searchParams.get("subRegion") : selectedOptions[routeOptionKey].subRegion);
  const [subRegions, setSubregions] = useState([]);
  // City or MSL depending on above choices
  const [group, setGroup] = useState(searchParams.get("group") ? searchParams.get("group") : selectedOptions[routeOptionKey].group);
  const [groups, setGroups] = useState([]);
  const [warehouseIds, setWarehouseIds] = useState([]);
  const [isLoadingData, setIsLoadingData] = useState(false);

  const warehouseParams = useWarehouseData();

  // Sets Regions when warehouse data is loaded
  useEffect(() => {
    try {
      setRegions([...new Set(warehouseParams.map(({ region_id }) => (mapRegion(region_id))))])
    }
    catch {
      setRegions([])
    }
  }, [setRegions, warehouseParams]);

  // Sets Countries
  useEffect(() => {
    let gatheredCountries = [];
    const sortedParams = warehouseParams ? [...warehouseParams].sort((a, b) => a.country_code.localeCompare(b.country_code)) : [];
    try {
      for (const warehouseParam of sortedParams) {
        if (mapRegion(warehouseParam.region_id) === region || region === "All") {
          gatheredCountries.push(warehouseParam.country_code)
        };
      };
    }
    catch {

    }
    gatheredCountries = [...new Set(gatheredCountries)];
    setCountries(gatheredCountries);
  }, [setCountries, region, warehouseParams]);

  // Sets Networks
  useEffect(() => {
    let gatheredNetworks = [];
    const sortedParams = warehouseParams ? [...warehouseParams].sort((a, b) => a.business_type.localeCompare(b.business_type)) : [];
    try {
      for (const warehouseParam of sortedParams) {
        if (
          (warehouseParam.country_code === country || country === "All")
          && (mapRegion(warehouseParam.region_id) === region || region === "All")
        ) {
          gatheredNetworks.push(mapNetwork(warehouseParam.business_type))
        };
      };
    }
    catch { }
    gatheredNetworks = [...new Set(gatheredNetworks)];
    setNetworks(gatheredNetworks);
  }, [setNetworks, country, warehouseParams, region]);

  // Sets SubRegions
  useEffect(() => {
    let gatheredSubRegions = [];
    try {
      for (const warehouseParam of warehouseParams) {
        if (
          (mapNetwork(warehouseParam.business_type) === network || network === "All")
          && (warehouseParam.country_code === country || country === "All")
          && (mapRegion(warehouseParam.region_id) === region || region === "All")
        ) {
          const subRegionValue = subRegionMap[warehouseParam.warehouse_id] || DEFAULT_SUB_REGION;
          gatheredSubRegions.push(subRegionValue);
        }
      }
    }
    catch { }
    gatheredSubRegions = [...new Set(gatheredSubRegions)];
    setSubregions(gatheredSubRegions);
  }, [setSubregions, network, warehouseParams, region, country]);

  // Sets Groups
  useEffect(() => {
    let gatheredGroups = [];
    const sortedParams = warehouseParams ? [...warehouseParams].sort((a, b) =>
      (multiSiteLeadMap[a.warehouse_id] || a.geography).localeCompare(multiSiteLeadMap[b.warehouse_id] || b.geography)
    ) : [];

    try {
      for (const warehouseParam of sortedParams) {
        const warehouseSubRegion = subRegionMap[warehouseParam.warehouse_id] || DEFAULT_SUB_REGION;
        if (
          (mapNetwork(warehouseParam.business_type) === network || network === "All")
          && (warehouseParam.country_code === country || country === "All")
          && (mapRegion(warehouseParam.region_id) === region || region === "All")
          && (warehouseSubRegion === subRegion || subRegion === "All")
        ) {
          // Use custom mapping if available, otherwise fall back to geography
          const groupValue = multiSiteLeadMap[warehouseParam.warehouse_id] || warehouseParam.geography;
          gatheredGroups.push(groupValue);
        }
      }
    }
    catch { }
    gatheredGroups = [...new Set(gatheredGroups)];
    setGroups(gatheredGroups);
  }, [setGroups, network, warehouseParams, region, country, subRegion]);

  // Sets Warehouse IDs
  useEffect(() => {
    let gatheredWarehouses = [];
    try {
      for (const warehouseParam of warehouseParams) {
        const warehouseGroup = multiSiteLeadMap[warehouseParam.warehouse_id] || warehouseParam.geography;
        const warehouseSubRegion = subRegionMap[warehouseParam.warehouse_id] || DEFAULT_SUB_REGION;
        if (
          (mapRegion(warehouseParam.region_id) === region || region === "All")
          && (warehouseParam.country_code === country || country === "All")
          && (mapNetwork(warehouseParam.business_type) === network || network === "All")
          && (warehouseSubRegion === subRegion || subRegion === "All")
          && (warehouseGroup === group || group === "All")
        ) {
          gatheredWarehouses.push(warehouseParam)
        }
      }
    }
    catch { }
    gatheredWarehouses = [...new Set(gatheredWarehouses)];
    setWarehouseIds(gatheredWarehouses);
  }, [region, country, network, subRegion, group, warehouseParams, setWarehouseIds]);

  // Clear loading state when warehouse IDs are updated after dropdown changes
  useEffect(() => {
    if (warehouseIds.length > 0 && isLoadingData) {
      // Add a small delay to ensure the NetworkScreen has time to start loading
      const timer = setTimeout(() => {
        setIsLoadingData(false);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [warehouseIds, isLoadingData]);

  // This is the navigation for the select region
  const navigateRegion = useCallback(
    value => {
      setIsLoadingData(true);
      const newRegion = value;
      const newSelectedOptions = JSON.parse(JSON.stringify(selectedOptions));
      newSelectedOptions[routeOptionKey].region = newRegion;
      setSelectedOptions(newSelectedOptions);
      setSearchParams({ "region": newRegion });
      setRegion(newRegion);
      setCountry(undefined);
      setNetwork(undefined);
      setSubRegion(undefined);
      setGroup(undefined);
    },
    // eslint-disable-next-line
    [setSearchParams, setRegion, setSelectedOptions]
  );

  // This is the navigation for the select country
  const navigateCountry = useCallback(
    value => {
      setIsLoadingData(true);
      const newCountry = value;
      const newSelectedOptions = JSON.parse(JSON.stringify(selectedOptions));
      newSelectedOptions[routeOptionKey].region = region;
      newSelectedOptions[routeOptionKey].country = newCountry;
      setSelectedOptions(newSelectedOptions);
      setSearchParams(
        {
          "region": region,
          "country": newCountry
        }
      );
      setCountry(newCountry);
      setNetwork(undefined);
      setSubRegion(undefined);
      setGroup(undefined);
    },
    // eslint-disable-next-line
    [setSearchParams, setCountry, region, setSelectedOptions]
  );

  // This is the navigation for the select network
  const navigateNetwork = useCallback(
    value => {
      setIsLoadingData(true);
      const newNetwork = value;
      const newSelectedOptions = JSON.parse(JSON.stringify(selectedOptions));
      newSelectedOptions[routeOptionKey].region = region;
      newSelectedOptions[routeOptionKey].country = country;
      newSelectedOptions[routeOptionKey].network = newNetwork;
      setSelectedOptions(newSelectedOptions);
      setSearchParams(
        {
          "region": region,
          "country": country,
          "network": newNetwork
        }
      );
      setNetwork(newNetwork);
      setSubRegion(undefined);
      setGroup(undefined);
    },
    // eslint-disable-next-line
    [setSearchParams, setNetwork, region, country, setSelectedOptions]
  );

  // This is the navigation for the select sub-region
  const navigateSubRegion = useCallback(
    value => {
      setIsLoadingData(true);
      const newSubRegion = value;
      const newSelectedOptions = JSON.parse(JSON.stringify(selectedOptions));
      newSelectedOptions[routeOptionKey].region = region;
      newSelectedOptions[routeOptionKey].country = country;
      newSelectedOptions[routeOptionKey].network = network;
      newSelectedOptions[routeOptionKey].subRegion = newSubRegion;
      setSelectedOptions(newSelectedOptions);
      setSearchParams(
        {
          "region": region,
          "country": country,
          "network": network,
          "subRegion": newSubRegion
        }
      );
      setSubRegion(newSubRegion);
      setGroup(undefined);
    },
    // eslint-disable-next-line
    [setSearchParams, setSubRegion, region, country, network, setSelectedOptions]
  );

  // This is the navigation for the select group
  const navigateGroup = useCallback(
    value => {
      setIsLoadingData(true);
      const newGroup = value;
      const newSelectedOptions = JSON.parse(JSON.stringify(selectedOptions));
      newSelectedOptions[routeOptionKey].region = region;
      newSelectedOptions[routeOptionKey].country = country;
      newSelectedOptions[routeOptionKey].network = network;
      newSelectedOptions[routeOptionKey].subRegion = subRegion;
      newSelectedOptions[routeOptionKey].group = newGroup;
      setSelectedOptions(newSelectedOptions);
      // Record selection
      Analytics.record({ name: 'Summary Network Selection', attributes: newSelectedOptions[routeOptionKey] });
      setSearchParams(
        {
          "region": region,
          "country": country,
          "network": network,
          "subRegion": subRegion,
          "group": newGroup
        }
      );
      setGroup(newGroup);
    },
    // eslint-disable-next-line
    [setSearchParams, setGroup, region, country, network, subRegion, setSelectedOptions]
  );

  return (
    <PageLayout
      title={""}
      loading={!warehouseParams}
      width={"inherit"}
      horizontallySticky={true}
      maxWidth={"none"}
      alignmentHorizontal={"start"}
    >
      <Column>
        <Row>
          {regions &&
            <Column>
              <Text>Region</Text>
              <Select
                value={region}
                onChange={navigateRegion}
                id="region-select"
                placeholder="Select a region"
              >
                {regions.length > 1 && <SelectOption key="All" label="All" value="All" />}
                {regions.map(region => (
                  <SelectOption key={region} label={region} value={region} />
                ))}
              </Select>
            </Column>
          }
          {countries.length > 0 &&
            <Column>
              <Text>Country</Text>
              <Select
                value={country}
                onChange={navigateCountry}
                id="country-select"
                placeholder="Select a country"
              >
                {countries.length > 1 && <SelectOption key="All" label="All" value="All" />}
                {countries.map(country => (
                  <SelectOption key={country} label={country} value={country} />
                ))}
              </Select>
            </Column>
          }
          {networks.length > 0 &&
            <Column>
              <Text>Network</Text>
              <Select
                value={network}
                onChange={navigateNetwork}
                id="network-select"
                placeholder="Select a network"
              >
                {networks.length > 1 && <SelectOption key="All" label="All" value="All" />}
                {networks.map(network => (
                  <SelectOption key={network} label={network} value={network} />
                ))}
              </Select>
            </Column>
          }
          {subRegions.length > 0 &&
            <Column>
              <Text>Sub-Region</Text>
              <Select
                value={subRegion}
                onChange={navigateSubRegion}
                id="sub-region-select"
                placeholder="Select a sub region"
              >
                {subRegions.length > 1 && <SelectOption key="All" label="All" value="All" />}
                {subRegions.map(subRegion => (
                  <SelectOption key={subRegion} label={subRegion} value={subRegion} />
                ))}
              </Select>
            </Column>
          }
          {groups.length > 0 &&
            <Column>
              <Text>Group</Text>
              <Select
                value={group}
                onChange={navigateGroup}
                id="group-select"
                placeholder="Select a group"
              >
                {groups.length > 1 && <SelectOption key="All" label="All" value="All" />}
                {groups.map(group => (
                  <SelectOption key={group} label={group} value={group} />
                ))}
              </Select>
            </Column>
          }
        </Row>
        {isLoadingData ? (
          <Column alignmentHorizontal="center" spacingInset="600 none">
            <Loader type="circular" label="Loading data..." />
          </Column>
        ) : (
          warehouseIds.length > 0 &&
          <NetworkScreen
            warehouseParams={warehouseIds}
          />
        )}
      </Column>
    </PageLayout>
  )
}

export default NetworkTab;
